import React, { useMemo } from "react";
import { Box, Button, CircularProgress, Typography, Tooltip } from "@mui/material";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { Simulation } from "../../../interfaces/DTO/simulation";
import { useSimulationsByContactIds } from "../../../hooks/feeLab/useSimulation";
import { formatSimulationStatus, isSelectedForDisplay } from "../../../utils/simulationUtil";
import StyledDrawer from "../../../shared/UIParts/StyledDrawer";
import SimulationPickerList from "./SimulationPickerList";

interface Props {
  contacts: Contact[];
  familyId?: number;
  selectedPeriod: string;
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  selectedSimulation: Simulation | null;
  onSelect: (sim: Simulation) => void;
  onExit: () => void;
  drawerOpen: boolean;
  onDrawerOpen: () => void;
  onDrawerClose: () => void;
  onCompare?: (sim: Simulation) => void;
}

function isWithinThreeMonths(a: string, b: string): boolean {
  const aYear = parseInt(a.slice(0, 4), 10);
  const aMonth = parseInt(a.slice(4, 6), 10);
  const bYear = parseInt(b.slice(0, 4), 10);
  const bMonth = parseInt(b.slice(4, 6), 10);
  return Math.abs((aYear * 12 + aMonth) - (bYear * 12 + bMonth)) <= 3;
}

const SimulationBar: React.FC<Props> = ({
  contacts,
  familyId,
  selectedPeriod,
  selectedPeriodType,
  selectedSimulation,
  onSelect,
  onExit,
  drawerOpen,
  onDrawerOpen,
  onDrawerClose,
  onCompare,
}) => {
  const contactIds = contacts.map((c) => c.id);

  const { data: simulations = [], isLoading } = useSimulationsByContactIds(
    contactIds,
    selectedPeriod
  );

  const suggestedSimulation = useMemo(() => {
    if (simulations.length === 0) return null;
    const eligible = simulations.filter((s) => {
      const isCurrentPeriod = s.reported_period === selectedPeriod;
      const isPreAdviceMatch =
        s.is_pre_advice &&
        selectedPeriodType === "PRE_ADVICE" &&
        isWithinThreeMonths(s.reported_period, selectedPeriod);
      return isCurrentPeriod || isPreAdviceMatch;
    });
    if (eligible.length === 0) return null;
    const selected = eligible.find((s) => isSelectedForDisplay(s.status));
    if (selected) return selected;
    return [...eligible].sort((a, b) =>
      (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at)
    )[0];
  }, [simulations, selectedPeriod, selectedPeriodType]);

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Button variant="contained" onClick={onDrawerOpen}>
          טען סימולציה
        </Button>

        {suggestedSimulation && !selectedSimulation && (
          <>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">או</Typography>
            <Tooltip title={`${formatSimulationStatus(suggestedSimulation.status)} • ${suggestedSimulation.updated_at?.slice(0, 10) ?? ""}`}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onSelect(suggestedSimulation)}
              >
                המשך לעבוד על &nbsp;|&nbsp; {formatSimulationStatus(suggestedSimulation.status)} &nbsp;|&nbsp; {suggestedSimulation.name || "ללא שם"}
              </Button>
            </Tooltip>
          </>
        )}

        {isLoading && <CircularProgress size={16} />}

        {selectedSimulation && (
          <>
            <Typography variant="subtitle1" color="primary" fontWeight="bold">
              סימולציה טעונה: {selectedSimulation.name}
            </Typography>
            <Button variant="contained" onClick={onExit}>
              חזור למצב רגיל
            </Button>
          </>
        )}
      </Box>

      <StyledDrawer open={drawerOpen} onClose={onDrawerClose} width={500}>
        <SimulationPickerList
          selectedPeriod={selectedPeriod}
          selectedPeriodType={selectedPeriodType}
          contacts={contacts}
          familyId={familyId}
          onSelect={(sim) => { onSelect(sim); onDrawerClose(); }}
          onCompare={onCompare ?? (() => {})}
        />
      </StyledDrawer>
    </>
  );
};

export default SimulationBar;
